import { motion } from 'framer-motion';
import { SITE } from '@/constants/testIds';

const chapters = [
  {
    n: '01',
    title: 'Only what the glass needs.',
    body:
      'A pane is a promise between a room and the world. We treat it with the reverence it deserves — no shortcuts, no film-forming detergents, no residue. Deionized water and hand-milled squeegees, calibrated to the hardness of Denver’s supply.',
    tag: 'The Ethic',
  },
  {
    n: '02',
    title: 'A wipe you can hear.',
    body:
      'That soft, clean squeak is our metric. Every pass is set at 32° pressure, drawn top-to-bottom, edges detailed by hand. If the light doesn’t catch it as invisible, we stay until it does.',
    tag: 'The Craft',
  },
  {
    n: '03',
    title: 'Leave nothing but light.',
    body:
      'Drop cloths inside. Zero-mark footpads on tile. Sills wiped, tracks vacuumed, screens rinsed and set back. When we’re done, only the view remains — the way the architect intended it.',
    tag: 'The Standard',
  },
];

export function Manifesto() {
  return (
    <section
      id="about"
      data-testid={SITE.manifestoSection}
      className="relative bg-background py-32 lg:py-48"
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-14">
        <div className="grid grid-cols-12 gap-6 mb-24">
          <div className="col-span-12 lg:col-span-4">
            <span className="label text-foreground/92">
              — About Us
            </span>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="col-span-12 lg:col-span-8"
          >
            <h2 className="font-heading font-light uppercase tracking-[-0.02em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Windows, done in a way
              <br />
              <span className="font-serif italic lowercase font-light">
                that feels almost architectural.
              </span>
            </h2>
          </motion.div>
        </div>

        <div className="space-y-24 lg:space-y-32">
          {chapters.map((c, i) => (
            <motion.article
              key={c.n}
              data-testid={SITE.manifestoChapter}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              className="grid grid-cols-12 gap-6 items-start"
            >
              <div
                className={`col-span-12 lg:col-span-5 ${
                  i % 2 === 1 ? 'lg:col-start-8 lg:order-2' : ''
                }`}
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-heading font-light text-[18vw] sm:text-[14vw] lg:text-[9vw] leading-none tracking-tighter text-[hsl(var(--sage))]">
                    {c.n}
                  </span>
                  <span className="label text-foreground/95">{c.tag}</span>
                </div>
              </div>
              <div
                className={`col-span-12 lg:col-span-6 ${
                  i % 2 === 1 ? 'lg:col-start-1 lg:order-1' : ''
                }`}
              >
                <h3 className="font-heading font-light text-3xl lg:text-5xl leading-[1.05] tracking-tight mb-6">
                  {c.title}
                </h3>
                <p className="text-base lg:text-lg leading-relaxed font-light text-foreground/92 max-w-xl">
                  {c.body}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
